/**
 * Le déclencheur au clavier d'une caméra dans un navigateur : Espace ou Entrée prennent la photo
 * (`<ActivityCopyCamera>`), comme un appui sur le rond. Sur le web seulement, et pour un appareil
 * qui a un clavier : au téléphone, rien n'est posé.
 *
 * Une touche ne déclenche pas quand elle a déjà un autre sens :
 *  - le focus est dans un champ où l'on écrit (un champ, une zone de texte, une liste, un texte
 *    modifiable) : l'espace y est une lettre ;
 *  - le focus est sur une commande (la croix, « Changer de caméra », le rond lui-même), que l'élève a
 *    atteinte à la tabulation : la touche l'actionne, elle, et le rond n'est pas pressé deux fois ;
 *  - une touche de modification est tenue, ou la touche se répète (tenue enfoncée).
 * À l'ouverture, le focus quitte la page restée dessous : le bouton qui a ouvert la caméra (« Prendre
 * ma copie en photo ») le gardait, et une touche l'aurait actionné sous la caméra.
 */
import { Platform } from "react-native"

/** Ce que l'écoute lit d'un appui : le sous-ensemble d'un `KeyboardEvent` du navigateur. */
export type ShutterKeyEvent = {
    key?: string
    repeat?: boolean
    altKey?: boolean
    ctrlKey?: boolean
    metaKey?: boolean
    shiftKey?: boolean
    target?: unknown
    preventDefault?: () => void
    stopPropagation?: () => void
}

/** Un nœud du document, tel que l'écoute le lit. */
type DomNode = {
    tagName?: string
    isContentEditable?: boolean
    closest?: (selector: string) => DomNode | null
    blur?: () => void
}

const EDITABLE_TAGS = new Set(["INPUT", "TEXTAREA", "SELECT"])
const CONTROL_SELECTOR =
    'button, a[href], [role="button"], [role="link"], [role="switch"], [role="checkbox"], [role="radio"], [role="tab"], ' +
    '[role="menuitem"], [role="option"]'

/** Le focus est dans un champ où l'on écrit. */
export function isEditableTarget(target: unknown): boolean {
    const node = target as DomNode | null | undefined
    if (node == null || typeof node !== "object") return false
    if (node.isContentEditable === true) return true
    return node.tagName != null && EDITABLE_TAGS.has(node.tagName.toUpperCase())
}

/**
 * Le focus a-t-il été laissé là par un clic ? Un bouton cliqué garde le focus dans le navigateur : Entrée
 * l'aurait actionné de nouveau (le quiz rebasculé), ou rien, au lieu de l'action de la surface (Wajih, 05/11 :
 * « après une génération, Entrée ne fait rien, parce que le focus reste sur le dernier bouton cliqué »). Le
 * pointeur qui descend le dit ; la tabulation rend la main au clavier. Lu sur le document, dès le chargement.
 */
let focusLeftByPointer = false
const watchedDocument = Platform.OS === "web" ? (globalThis as { document?: WebDocument }).document : undefined
watchedDocument?.addEventListener(
    "pointerdown",
    () => {
        focusLeftByPointer = true
    },
    true
)
watchedDocument?.addEventListener(
    "keydown",
    (event) => {
        if (event.key === "Tab") focusLeftByPointer = false
    },
    true
)

/** Le focus est sur une commande (un bouton, un lien, un interrupteur) : Entrée et Espace l'actionnent, elle. */
export function isControlTarget(target: unknown): boolean {
    return (target as DomNode | null | undefined)?.closest?.(CONTROL_SELECTOR) != null
}

/**
 * Une commande atteinte au clavier garde Entrée ; une commande où un clic a laissé le focus la rend à la surface.
 * Lu par `listenEnterKey` seul : la caméra de l'élève (`listenShutterKey`) laisse toute commande à elle-même.
 */
export function isKeyboardReachedControl(target: unknown): boolean {
    return !focusLeftByPointer && isControlTarget(target)
}

/** L'appui prend la photo : Espace ou Entrée, seuls, une fois, hors d'un champ et d'une commande. */
export function isShutterKey(event: ShutterKeyEvent): boolean {
    if (event.key !== " " && event.key !== "Enter") return false
    if (event.repeat === true) return false
    if (event.altKey === true || event.ctrlKey === true || event.metaKey === true || event.shiftKey === true) {
        return false
    }
    if (isEditableTarget(event.target)) return false
    return !isControlTarget(event.target)
}

type WebDocument = {
    activeElement?: DomNode | null
    addEventListener: (type: string, listener: (event: ShutterKeyEvent) => void, capture?: boolean) => void
    removeEventListener: (type: string, listener: (event: ShutterKeyEvent) => void, capture?: boolean) => void
}

/**
 * Écoute Espace et Entrée sur le document (web seulement), et retire le focus de la page du dessous.
 * Rend de quoi détacher l'écoute, à appeler au nettoyage de l'effet qui l'a posée.
 */
export function listenShutterKey(onShutter: () => void): () => void {
    const doc = Platform.OS === "web" ? (globalThis as { document?: WebDocument }).document : undefined
    if (doc == null) return () => undefined
    doc.activeElement?.blur?.()
    // La touche prise ne va pas plus loin, son relâchement non plus : un bouton du dessous qui a le focus
    // s'actionne au relâchement d'Espace.
    let swallowUp: string | null = null
    const onKeyDown = (event: ShutterKeyEvent) => {
        if (!isShutterKey(event)) return
        event.preventDefault?.()
        event.stopPropagation?.()
        swallowUp = event.key ?? null
        onShutter()
    }
    const onKeyUp = (event: ShutterKeyEvent) => {
        if (swallowUp == null || event.key !== swallowUp) return
        swallowUp = null
        event.preventDefault?.()
        event.stopPropagation?.()
    }
    // À la capture : avant le bouton qui a le focus.
    doc.addEventListener("keydown", onKeyDown, true)
    doc.addEventListener("keyup", onKeyUp, true)
    return () => {
        doc.removeEventListener("keydown", onKeyDown, true)
        doc.removeEventListener("keyup", onKeyUp, true)
    }
}

/**
 * Entrée, l'action d'une surface (générer, envoyer la proposition) : sur le document (web seulement), hors d'une
 * saisie et d'une commande atteinte au clavier. `act` rend vrai quand il a pris la touche : elle ne va alors pas
 * plus loin, et le bouton où un clic a laissé le focus ne s'actionne pas en plus. Rend de quoi détacher l'écoute.
 */
export function listenEnterKey(act: () => boolean): () => void {
    const doc = Platform.OS === "web" ? (globalThis as { document?: WebDocument }).document : undefined
    if (doc == null) return () => undefined
    let swallowUp = false
    const onKeyDown = (event: ShutterKeyEvent) => {
        if (event.key !== "Enter" || event.repeat === true || event.shiftKey === true) return
        if (isEditableTarget(event.target) || isKeyboardReachedControl(event.target)) return
        if (!act()) return
        event.preventDefault?.()
        event.stopPropagation?.()
        swallowUp = true
    }
    const onKeyUp = (event: ShutterKeyEvent) => {
        if (!swallowUp || event.key !== "Enter") return
        swallowUp = false
        event.preventDefault?.()
        event.stopPropagation?.()
    }
    // À la capture : avant le bouton qui a le focus (react-native-web l'actionne à Entrée, quel que soit son rôle).
    doc.addEventListener("keydown", onKeyDown, true)
    doc.addEventListener("keyup", onKeyUp, true)
    return () => {
        doc.removeEventListener("keydown", onKeyDown, true)
        doc.removeEventListener("keyup", onKeyUp, true)
    }
}

/**
 * L'appareil a un clavier : un navigateur dont le pointeur est une souris ou un pavé (fin, qui
 * survole). Un téléphone ou une tablette sans clavier n'ont qu'un doigt : faux. Hors du web, faux.
 */
export function hasPhysicalKeyboard(): boolean {
    if (Platform.OS !== "web") return false
    const matchMedia = (globalThis as { matchMedia?: (query: string) => { matches: boolean } }).matchMedia
    return matchMedia?.("(hover: hover) and (pointer: fine)").matches === true
}
